import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import { 
  ArrowLeft, Mail, Users, Trash2, Send, Calendar, Clock, Megaphone, 
  History, CheckCircle, XCircle, RefreshCw, Ban, Search, Unlock
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminMailingList() {
  const { getAuthHeader } = useAuth();
  const [subscribers, setSubscribers] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('subscribers');
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCount, setActiveCount] = useState(0);
  
  // Promo email state
  const [promoDialog, setPromoDialog] = useState(false);
  const [promoForm, setPromoForm] = useState({
    subject: '',
    body: '',
    button_text: '',
    button_url: ''
  });
  const [sendingPromo, setSendingPromo] = useState(false);
  
  // Drop update state
  const [dropDialog, setDropDialog] = useState(false);
  const [dropForm, setDropForm] = useState({
    drop_name: '',
    drop_date: '',
    drop_time: '',
    description: ''
  });
  const [sendingDrop, setSendingDrop] = useState(false);

  const fetchData = async (search = '') => {
    try {
      const searchParam = search ? `?search=${encodeURIComponent(search)}` : '';
      const [subsRes, campaignsRes] = await Promise.all([
        axios.get(`${API}/admin/mailing-list${searchParam}`, { headers: getAuthHeader() }),
        axios.get(`${API}/admin/email-campaigns`, { headers: getAuthHeader() })
      ]);
      setSubscribers(subsRes.data.subscribers || []);
      setActiveCount(subsRes.data.active_count || 0);
      setCampaigns(campaignsRes.data.campaigns || []);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchData(searchQuery);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleDeleteSubscriber = async (email, block = false) => {
    const action = block ? 'block' : 'unsubscribe';
    const message = block 
      ? `Block ${email} from the BLK LIST? They won't be able to re-subscribe.`
      : `Unsubscribe ${email} from the BLK LIST?`;
    
    if (!window.confirm(message)) return;
    
    try {
      await axios.delete(`${API}/admin/mailing-list/${encodeURIComponent(email)}?block_resubscribe=${block}`, {
        headers: getAuthHeader()
      });
      toast.success(block ? 'Subscriber blocked' : 'Subscriber removed');
      fetchData(searchQuery);
    } catch (error) {
      toast.error(`Failed to ${action} subscriber`);
    }
  };

  const handleUnblock = async (email) => {
    if (!window.confirm(`Unblock ${email}? They will be able to re-subscribe.`)) return;
    
    try {
      await axios.post(`${API}/admin/mailing-list/unblock/${encodeURIComponent(email)}`, {}, {
        headers: getAuthHeader()
      });
      toast.success('Subscriber unblocked');
      fetchData(searchQuery);
    } catch (error) {
      toast.error('Failed to unblock subscriber');
    }
  };

  const handleSendPromo = async () => {
    if (!promoForm.subject || !promoForm.body) {
      toast.error('Subject and body are required');
      return;
    }
    
    setSendingPromo(true);
    try {
      const response = await axios.post(`${API}/admin/mailing-list/send-promo`, promoForm, {
        headers: getAuthHeader()
      });
      toast.success(`Queued for ${response.data.queued ?? response.data.sent ?? 0} subscribers. Emails are sending in the background.`);
      setPromoDialog(false);
      setPromoForm({ subject: '', body: '', button_text: '', button_url: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send');
    } finally {
      setSendingPromo(false);
    }
  };

  const handleSendDropUpdate = async () => {
    if (!dropForm.drop_name || !dropForm.drop_date || !dropForm.drop_time) {
      toast.error('Drop name, date, and time are required');
      return;
    }
    
    setSendingDrop(true);
    try {
      const response = await axios.post(`${API}/admin/mailing-list/send-drop-update`, dropForm, {
        headers: getAuthHeader()
      });
      toast.success(`Queued for ${response.data.queued ?? response.data.sent ?? 0} subscribers. Emails are sending in the background.`);
      setDropDialog(false);
      setDropForm({ drop_name: '', drop_date: '', drop_time: '', description: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send');
    } finally {
      setSendingDrop(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-tiffany-500 font-mono">LOADING...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <Link 
              to="/admin" 
              className="text-zinc-500 hover:text-tiffany-500 font-mono text-sm flex items-center gap-2 mb-4"
            >
              <ArrowLeft className="w-4 h-4" />
              BACK TO ADMIN
            </Link>
            <h1 className="text-3xl font-sans font-bold text-white flex items-center gap-3">
              <Mail className="w-8 h-8 text-tiffany-500" />
              BLK LIST
            </h1>
            <p className="text-zinc-500 font-mono text-sm mt-1">
              {activeCount} active subscribers
            </p>
          </div>
          <div className="flex flex-wrap gap-2 sm:gap-3">
            <Button
              onClick={() => setDropDialog(true)}
              size="sm"
              className="bg-yellow-500 hover:bg-yellow-400 text-black font-mono rounded-none text-xs sm:text-sm"
              data-testid="send-drop-btn"
            >
              <Calendar className="w-4 h-4 mr-2" />
              DROP ALERT
            </Button>
            <Button
              onClick={() => setPromoDialog(true)}
              size="sm"
              className="bg-tiffany-500 hover:bg-tiffany-400 text-black font-mono rounded-none text-xs sm:text-sm"
              data-testid="send-promo-btn"
            >
              <Megaphone className="w-4 h-4 mr-2" />
              SEND PROMO
            </Button>
          </div>
        </div>

        {/* Search and Tabs */}
        <div className="mb-6">
          {/* Search Bar */}
          <div className="relative mb-4 flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-zinc-500" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by email..."
                className="bg-zinc-900 border-zinc-800 text-white font-mono rounded-none pl-10 pr-10"
                data-testid="mailing-list-search"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-zinc-500 hover:text-white"
                  data-testid="mailing-list-search-clear"
                >
                  <XCircle className="w-4 h-4" />
                </button>
              )}
            </div>
            <Button
              onClick={() => fetchData(searchQuery)}
              className="bg-tiffany-500 hover:bg-tiffany-400 text-black font-mono rounded-none px-4"
              data-testid="mailing-list-search-btn"
            >
              <Search className="w-4 h-4" />
            </Button>
          </div>
          
          {/* Tabs */}
          <div className="flex gap-4 border-b border-zinc-800">
            <button
              onClick={() => setActiveTab('subscribers')}
              className={`pb-3 px-2 font-mono text-sm transition-colors ${
                activeTab === 'subscribers' 
                  ? 'text-tiffany-500 border-b-2 border-tiffany-500' 
                  : 'text-zinc-500 hover:text-white'
              }`}
            >
              <Users className="w-4 h-4 inline mr-2" />
              SUBSCRIBERS ({activeCount})
            </button>
            <button
              onClick={() => setActiveTab('history')}
              className={`pb-3 px-2 font-mono text-sm transition-colors ${
                activeTab === 'history' 
                  ? 'text-tiffany-500 border-b-2 border-tiffany-500' 
                  : 'text-zinc-500 hover:text-white'
              }`}
            >
              <History className="w-4 h-4 inline mr-2" />
              CAMPAIGN HISTORY ({campaigns.length})
            </button>
          </div>
        </div>

        {/* Subscribers Tab */}
        {activeTab === 'subscribers' && (
          <div className="space-y-2">
            {subscribers.length === 0 ? (
              <div className="text-center py-12 text-zinc-500 font-mono">
                {searchQuery ? 'No subscribers found matching your search' : 'No subscribers yet'}
              </div>
            ) : (
              subscribers.map((sub, idx) => {
                const isBlocked = sub.blocked;
                const isUnsubscribed = sub.unsubscribed && !sub.blocked;
                
                return (
                  <div 
                    key={idx}
                    className={`flex flex-col sm:flex-row sm:items-center justify-between p-3 sm:p-4 border gap-2 ${
                      isBlocked 
                        ? 'bg-red-950/30 border-red-900/50' 
                        : isUnsubscribed
                          ? 'bg-zinc-900/50 border-zinc-800/50'
                          : 'bg-zinc-900 border-zinc-800'
                    }`}
                    data-testid={`subscriber-${idx}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <p className={`font-mono text-sm break-all ${isBlocked ? 'text-red-400' : isUnsubscribed ? 'text-zinc-500' : 'text-white'}`}>
                          {sub.email}
                        </p>
                        {isBlocked && (
                          <Badge className="rounded-none border-0 text-[10px] bg-red-500/20 text-red-400 flex-shrink-0">
                            BLOCKED
                          </Badge>
                        )}
                        {isUnsubscribed && (
                          <Badge className="rounded-none border-0 text-[10px] bg-zinc-700/50 text-zinc-400 flex-shrink-0">
                            UNSUBSCRIBED
                          </Badge>
                        )}
                      </div>
                      <p className="font-mono text-[10px] sm:text-xs text-zinc-500 mt-1">
                        Joined: {sub.subscribed_at ? new Date(sub.subscribed_at).toLocaleDateString() : 'N/A'}
                        {sub.unsubscribed_at && (
                          <span className="ml-2">
                            • Left: {new Date(sub.unsubscribed_at).toLocaleDateString()}
                          </span>
                        )}
                      </p>
                    </div>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {isBlocked ? (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleUnblock(sub.email)}
                          className="text-green-400 hover:text-green-300 hover:bg-green-500/10 h-8 px-2 gap-1"
                          title="Unblock user"
                        >
                          <Unlock className="w-4 h-4" />
                          <span className="text-xs font-mono hidden sm:inline">UNBLOCK</span>
                        </Button>
                      ) : isUnsubscribed ? (
                        <span className="text-zinc-600 font-mono text-xs">Can re-subscribe</span>
                      ) : (
                        <>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteSubscriber(sub.email, false)}
                            className="text-zinc-400 hover:text-yellow-500 h-8 w-8 p-0"
                            title="Unsubscribe (can re-subscribe)"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteSubscriber(sub.email, true)}
                            className="text-zinc-400 hover:text-red-500 h-8 w-8 p-0"
                            title="Block (cannot re-subscribe)"
                          >
                            <Ban className="w-4 h-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* Campaign History Tab */}
        {activeTab === 'history' && (
          <div className="space-y-3">
            {campaigns.length === 0 ? (
              <div className="text-center py-12 text-zinc-500 font-mono">
                No campaigns sent yet
              </div>
            ) : (
              campaigns.map((campaign, idx) => (
                <div 
                  key={idx}
                  className="p-4 bg-zinc-900 border border-zinc-800"
                  data-testid={`campaign-${idx}`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className={`rounded-none border-0 text-xs ${
                          campaign.type === 'drop_update' 
                            ? 'bg-yellow-500/20 text-yellow-500' 
                            : 'bg-tiffany-500/20 text-tiffany-500'
                        }`}>
                          {campaign.type === 'drop_update' ? 'DROP ALERT' : 'PROMO'}
                        </Badge>
                        <span className="font-mono text-white text-sm">
                          {campaign.subject || campaign.drop_name}
                        </span>
                      </div>
                      <p className="font-mono text-xs text-zinc-500">
                        Sent by {campaign.sent_by} on {new Date(campaign.sent_at).toLocaleString()}
                      </p>
                    </div>
                    <div className="text-right">
                      <div className="flex items-center gap-3">
                        <span className="font-mono text-sm text-green-500 flex items-center gap-1">
                          <CheckCircle className="w-3 h-3" />
                          {campaign.sent}
                        </span>
                        {campaign.failed > 0 && (
                          <span className="font-mono text-sm text-red-500 flex items-center gap-1">
                            <XCircle className="w-3 h-3" />
                            {campaign.failed}
                          </span>
                        )}
                      </div>
                      <p className="font-mono text-xs text-zinc-600 mt-1">
                        of {campaign.recipients} recipients
                      </p>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Promo Email Dialog */}
        <Dialog open={promoDialog} onOpenChange={setPromoDialog}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-white font-sans flex items-center gap-2">
                <Megaphone className="w-5 h-5 text-tiffany-500" />
                Send Promo Email
              </DialogTitle>
              <DialogDescription className="text-zinc-400 font-mono text-sm">
                This will send to all {activeCount} active subscribers
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="text-xs font-mono text-zinc-400 mb-1 block">SUBJECT *</label>
                <Input
                  value={promoForm.subject}
                  onChange={(e) => setPromoForm({...promoForm, subject: e.target.value})}
                  placeholder="e.g., New Items Added!"
                  className="bg-zinc-800 border-zinc-700 text-white font-mono rounded-none"
                />
              </div>
              <div>
                <label className="text-xs font-mono text-zinc-400 mb-1 block">MESSAGE *</label>
                <Textarea
                  value={promoForm.body}
                  onChange={(e) => setPromoForm({...promoForm, body: e.target.value})}
                  placeholder="Write your message here..."
                  rows={5}
                  className="bg-zinc-800 border-zinc-700 text-white font-mono rounded-none"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-mono text-zinc-400 mb-1 block">BUTTON TEXT</label>
                  <Input
                    value={promoForm.button_text}
                    onChange={(e) => setPromoForm({...promoForm, button_text: e.target.value})}
                    placeholder="e.g., SHOP NOW"
                    className="bg-zinc-800 border-zinc-700 text-white font-mono rounded-none"
                  />
                </div>
                <div>
                  <label className="text-xs font-mono text-zinc-400 mb-1 block">BUTTON URL</label>
                  <Input
                    value={promoForm.button_url}
                    onChange={(e) => setPromoForm({...promoForm, button_url: e.target.value})}
                    placeholder="https://..."
                    className="bg-zinc-800 border-zinc-700 text-white font-mono rounded-none"
                  />
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setPromoDialog(false)}
                className="rounded-none border-zinc-700 text-zinc-400"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSendPromo}
                disabled={sendingPromo}
                className="bg-tiffany-500 hover:bg-tiffany-400 text-black font-mono rounded-none"
              >
                {sendingPromo ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                {sendingPromo ? 'SENDING...' : 'SEND EMAIL'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Drop Update Dialog */}
        <Dialog open={dropDialog} onOpenChange={setDropDialog}>
          <DialogContent className="bg-zinc-900 border-zinc-800 max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-white font-sans flex items-center gap-2">
                <Calendar className="w-5 h-5 text-yellow-500" />
                Send Drop Alert
              </DialogTitle>
              <DialogDescription className="text-zinc-400 font-mono text-sm">
                Notify {activeCount} active subscribers about an upcoming drop
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <label className="text-xs font-mono text-zinc-400 mb-1 block">DROP NAME *</label>
                <Input
                  value={dropForm.drop_name}
                  onChange={(e) => setDropForm({...dropForm, drop_name: e.target.value})}
                  placeholder="e.g., Winter Collection 2026"
                  className="bg-zinc-800 border-zinc-700 text-white font-mono rounded-none"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs font-mono text-zinc-400 mb-1 block">DATE *</label>
                  <Input
                    value={dropForm.drop_date}
                    onChange={(e) => setDropForm({...dropForm, drop_date: e.target.value})}
                    placeholder="e.g., Saturday, Feb 15"
                    className="bg-zinc-800 border-zinc-700 text-white font-mono rounded-none"
                  />
                </div>
                <div>
                  <label className="text-xs font-mono text-zinc-400 mb-1 block">TIME *</label>
                  <Input
                    value={dropForm.drop_time}
                    onChange={(e) => setDropForm({...dropForm, drop_time: e.target.value})}
                    placeholder="e.g., 8:00 PM EST"
                    className="bg-zinc-800 border-zinc-700 text-white font-mono rounded-none"
                  />
                </div>
              </div>
              <div>
                <label className="text-xs font-mono text-zinc-400 mb-1 block">DESCRIPTION</label>
                <Textarea
                  value={dropForm.description}
                  onChange={(e) => setDropForm({...dropForm, description: e.target.value})}
                  placeholder="What's dropping? Hype it up..."
                  rows={3}
                  className="bg-zinc-800 border-zinc-700 text-white font-mono rounded-none"
                />
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setDropDialog(false)}
                className="rounded-none border-zinc-700 text-zinc-400"
              >
                Cancel
              </Button>
              <Button
                onClick={handleSendDropUpdate}
                disabled={sendingDrop}
                className="bg-yellow-500 hover:bg-yellow-400 text-black font-mono rounded-none"
              >
                {sendingDrop ? (
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Send className="w-4 h-4 mr-2" />
                )}
                {sendingDrop ? 'SENDING...' : 'SEND ALERT'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
